import type { i18n as I18n } from 'i18next';

import { formatDateTimeWorded } from '@/utils/date';
import hasOwnKey from '@/utils/hasOwnKey';

/**
 * `{{lastMeasurement, date}}`: the worded display date-time in the language being rendered,
 * or nothing for an empty value. That is the language i18next passes, which a fixed `t`
 * (`getFixedT('nb')`) can set apart from the page language.
 */
export function formatDateValue(value: unknown, language?: string): string {
    if (!value) return String(value ?? '');
    if (typeof value === 'string' || typeof value === 'number' || value instanceof Date) {
        return formatDateTimeWorded(value, 'second', language);
    }
    return '';
}

/**
 * Registers the formats translations use after `{{value, …}}`. i18next sets its formatter up
 * in `init()`, which resets the registered formats, so this runs after it.
 */
export function registerFormatters(instance: I18n): void {
    const { formatter } = instance.services;
    if (!formatter) throw new Error('registerFormatters runs after i18next init()');
    formatter.add('date', formatDateValue);
}

/**
 * i18next's formatter hands a missing value back untouched, so a translation would print a raw
 * `{{ignoredUntil, date}}` when the variable is not passed. The format function this setup used
 * before i18next 26 printed nothing there, and this keeps that. Every other placeholder gets
 * i18next's own handling: blank when passed as undefined, left as written when not passed.
 */
export function missingInterpolation(_text: string, match: unknown, options: unknown): string {
    if (!Array.isArray(match) || typeof match[0] !== 'string' || typeof match[1] !== 'string') {
        return '';
    }
    const name = match[1].trim();
    if (name.includes(',')) return '';
    if (typeof options === 'object' && options !== null && hasOwnKey(options, name)) return '';
    return match[0];
}
